import { MailtoParams } from "@platform/fe-utils";

import { formatDate } from "../../../../utils.js";
import { formatResponseDate } from "../../../../utils/common";

import { CarrierEmailOptionKey, EmailMenuOptionKey } from "./EmailMenuButton";

export type EmailTemplateContext = {
  rfpSubmitterEmail: string;
  rfpSubmitterName: string;
  clientExecutiveEmail?: string | null;
  analystName?: string;
  groupName?: string | null;
  effectiveDate: string;
  linesOfCoverage?: readonly string[];
};

type EmailTemplate = (context: EmailTemplateContext) => MailtoParams;

// Left in the draft when the RFP has no assigned analyst, so the gap reads as
// something to fill in rather than as a missing signature.
const ANALYST_NAME_PLACEHOLDER = "[Assigned Analyst Name]";
const GROUP_NAME_PLACEHOLDER = "[Group Name]";
const LINES_OF_COVERAGE_PLACEHOLDER = "[Line 1, Line 2, and Line 3]";
const THANK_YOU_SIGN_OFF = "Thank you!";

const resolveGroupName = (groupName?: string | null): string =>
  groupName || GROUP_NAME_PLACEHOLDER;

const resolveLinesOfCoverage = (linesOfCoverage?: readonly string[]): string =>
  linesOfCoverage?.length
    ? new Intl.ListFormat("en-US", { type: "conjunction" }).format(
        linesOfCoverage,
      )
    : LINES_OF_COVERAGE_PLACEHOLDER;

const buildCc = (clientExecutiveEmail?: string | null): string[] =>
  clientExecutiveEmail ? [clientExecutiveEmail] : [];

/** `subjectNote` names the milestone in subjects that carry one. */
const buildSubject = (
  { groupName, effectiveDate }: EmailTemplateContext,
  subjectNote?: string,
): string =>
  [
    "RFP",
    resolveGroupName(groupName),
    subjectNote,
    `Effective Date ${effectiveDate}`,
  ]
    .filter(Boolean)
    .join(" - ");

const buildGreeting = (rfpSubmitterName: string): string =>
  `Good morning/afternoon ${rfpSubmitterName},`;

const buildAssignmentLine = ({
  groupName,
  effectiveDate,
}: EmailTemplateContext): string =>
  `Thank you for the opportunity to assist! I am the analyst assigned to ${resolveGroupName(groupName)}, effective ${effectiveDate}.`;

const buildConfirmReceipt: EmailTemplate = (context) => ({
  to: [context.rfpSubmitterEmail],
  cc: buildCc(context.clientExecutiveEmail),
  subject: buildSubject(context),
  body: [
    buildGreeting(context.rfpSubmitterName),
    "",
    buildAssignmentLine(context),
    "",
    "This email is to confirm receipt of your RFP. I am in the process of reviewing the documents and will be in touch shortly should I have any questions and/or when the RFP is released to the market.",
    "",
    "Please let me know if you have any questions or if I can assist with anything in the meantime.",
    "",
    THANK_YOU_SIGN_OFF,
    "",
    context.analystName ?? ANALYST_NAME_PLACEHOLDER,
  ].join("\n"),
});

export const RUSH_NOTE =
  "I see there is a tight timeline on this RFP and completely understand the urgency. I can request a rush with the carriers and will do my best to expedite. Our standard turnaround time is 12-14 business days, but I can forward the proposals to you as I receive them with the understanding that they may change as we vet and work with carriers. That way you have something in hand to review while we're working through proposals and getting them added onto the marketing results. We will need 2-3 business days after the last proposal is received to finalize your presentation.";

/**
 * The rush variant is the standard questions email with one extra paragraph
 * before the closing, so both are built from the same copy.
 */
const createCompileQuestions =
  (rushNote?: string): EmailTemplate =>
  (context) => ({
    to: [context.rfpSubmitterEmail],
    cc: buildCc(context.clientExecutiveEmail),
    subject: buildSubject(context),
    body: [
      buildGreeting(context.rfpSubmitterName),
      "",
      `${buildAssignmentLine(context)} In order to release the RFP to market we will need the following items.`,
      "",
      "REQUIRED ITEMS TO RELEASE RFP TO MARKET:",
      "[- required item, one per line]",
      "",
      "ITEMS NEEDED FOR FINAL ANALYSIS:",
      "[- final analysis item, one per line]",
      "",
      ...(rushNote ? [rushNote, ""] : []),
      "Once the required items have been received, we will release the RFP to market. Please note, any major delays in receipt of this information may result in a delayed due date.",
      "",
      "Please let me know if you have any questions!",
      "",
      "Thanks!",
      "",
      context.analystName ?? ANALYST_NAME_PLACEHOLDER,
    ].join("\n"),
  });

const buildCompileQuestions = createCompileQuestions();
const buildCompileQuestionsRush = createCompileQuestions(RUSH_NOTE);

const buildCloseOpportunityMissingData: EmailTemplate = (context) => ({
  to: [context.rfpSubmitterEmail],
  cc: buildCc(context.clientExecutiveEmail),
  subject: buildSubject(context, "Opportunity Closed"),
  body: [
    `Hello ${context.rfpSubmitterName},`,
    "",
    `Due to missing information, we will be closing the opportunity for ${resolveGroupName(context.groupName)}, effective ${context.effectiveDate}. We will be happy to re-open if missing information is received.`,
    "",
    "We value your partnership and if you have any questions or concerns, please let me know.",
    "",
    THANK_YOU_SIGN_OFF,
    "",
    context.analystName ?? ANALYST_NAME_PLACEHOLDER,
  ].join("\n"),
});

const buildSoldCaseFollowUp: EmailTemplate = (context) => ({
  to: [context.rfpSubmitterEmail],
  cc: buildCc(context.clientExecutiveEmail),
  subject: buildSubject(context, "Sold Case Confirmation"),
  body: [
    `Hi ${context.rfpSubmitterName},`,
    "",
    `I hope you are doing well. I'm reaching out to confirm if final decisions for ${resolveGroupName(context.groupName)} have been made for the following lines of coverage:`,
    resolveLinesOfCoverage(context.linesOfCoverage),
    `effective ${context.effectiveDate}.`,
    "",
    "Would you please confirm the final selected carrier and plan option for each line of coverage so we can document this in our records?",
    "",
    "Thank you kindly,",
    "",
    context.analystName ?? ANALYST_NAME_PLACEHOLDER,
  ].join("\n"),
});

export type CarrierEmailContext = {
  carrierName: string;
  salesRepEmails: readonly string[];
  clientExecutiveEmail?: string | null;
  analystName?: string;
  groupName?: string | null;
  carrierDueDate?: string | null;
  today: Date;
};

const CARRIER_DUE_DATE_PLACEHOLDER = "[Carrier Due Date]";

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * The API stores the carrier due date as midnight UTC, so only its calendar
 * date is compared against the analyst's local today.
 */
const buildProposalDueStatus = (
  carrierDueDate: Date | null,
  today: Date,
): string => {
  if (!carrierDueDate) {
    return `[are due today / were due ${CARRIER_DUE_DATE_PLACEHOLDER}]`;
  }

  const dueTime = carrierDueDate.getTime();
  const todayTime = startOfDay(today).getTime();

  if (dueTime === todayTime) {
    return "are due today";
  }

  const dueVerb = dueTime < todayTime ? "were" : "are";
  return `${dueVerb} due ${formatDate(carrierDueDate)}`;
};

type CarrierEmailTemplate = (context: CarrierEmailContext) => MailtoParams;

const buildCarrierFollowUp: CarrierEmailTemplate = (context) => {
  const groupName = resolveGroupName(context.groupName);
  const carrierDueDate = formatResponseDate(context.carrierDueDate ?? null);

  return {
    to: [...context.salesRepEmails],
    cc: buildCc(context.clientExecutiveEmail),
    subject: [
      "RFP",
      groupName,
      "Proposal Follow Up",
      `DUE ${formatDate(carrierDueDate) ?? CARRIER_DUE_DATE_PLACEHOLDER}`,
    ].join(" - "),
    body: [
      "Hi,",
      "",
      `Proposals for ${groupName} ${buildProposalDueStatus(carrierDueDate, context.today)}. I'm reaching out to see if the proposal is ready and if not if you could provide an ETA. If you have any questions, please feel free to reach out!`,
      "",
      "Thank you kindly,",
      "",
      context.analystName ?? ANALYST_NAME_PLACEHOLDER,
    ].join("\n"),
  };
};

const buildCarrierConfirmations: CarrierEmailTemplate = (context) => ({
  to: [...context.salesRepEmails],
  cc: buildCc(context.clientExecutiveEmail),
  subject: [
    "RFP",
    resolveGroupName(context.groupName),
    `${context.carrierName} Confirmations`,
  ].join(" - "),
  body: [
    "Hi,",
    "",
    "Thank you for your proposal! I had a few questions I'm hoping you could confirm for me:",
    "",
    "General:",
    "[- General question]",
    "",
    "[Line Name]:",
    "[- Line-specific question]",
    "",
    "Thanks so much for your help!",
    "",
    context.analystName ?? ANALYST_NAME_PLACEHOLDER,
  ].join("\n"),
});

export const CARRIER_EMAIL_TEMPLATES: Record<
  CarrierEmailOptionKey,
  CarrierEmailTemplate
> = {
  "carrier-follow-up": buildCarrierFollowUp,
  "compile-email-to-carrier": buildCarrierConfirmations,
};

const buildMarketingStatusUpdate: EmailTemplate = (context) => ({
  to: [context.rfpSubmitterEmail],
  cc: buildCc(context.clientExecutiveEmail),
  subject: buildSubject(context, "Marketing Status Update"),
  body: [
    buildGreeting(context.rfpSubmitterName),
    "",
    `I am reaching out to provide you a quick status update for ${resolveGroupName(context.groupName)}. At this time, we have received the following Carrier proposals:`,
    "",
    "[- Carrier(s) with proposals received — one per line; section omitted if none]",
    "",
    "[I am currently still pending a response from [Carrier(s)] — only if at least one is still pending]",
    "",
    "Would you like me to provide you with everything received by the due date; or would you prefer to give Carriers a few extra days and receive all data at once? Please let me know what works best for you and your team.",
    "",
    "Please let me know if you have any questions or if there is anything additional I can do to help at this time.",
    "",
    THANK_YOU_SIGN_OFF,
    "",
    context.analystName ?? ANALYST_NAME_PLACEHOLDER,
  ].join("\n"),
});

// TODO: In the incoming tickets map new options here
export const EMAIL_TEMPLATES: Partial<
  Record<EmailMenuOptionKey, EmailTemplate>
> = {
  "confirm-receipt": buildConfirmReceipt,
  "compile-questions-to-rfp-submitter": buildCompileQuestions,
  "compile-questions-to-rfp-submitter-rush": buildCompileQuestionsRush,
  "close-opportunity-missing-data": buildCloseOpportunityMissingData,
  "sold-case-follow-up": buildSoldCaseFollowUp,
  "marketing-status-update": buildMarketingStatusUpdate,
};
